import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@/components/controls/Modal";
import Text from "@/components/ui/text";
import IconContainer from "@/components/ui/icon-container";
import { Alert } from "@dreamhub/designsystem/icons";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

interface Props {
  isOpen: boolean;
  isSaving: boolean;
  /** A pending line ends earlier than the terms it came from. */
  shortensService?: boolean;
  onKeepEditing: () => void;
  onSave: (amendmentReason: string) => void;
}

// Collects the optional free-text amendment reason; an empty one is sent as null.
const SaveTermsModal: React.FC<Props> = ({
  isOpen,
  isSaving,
  shortensService = false,
  onKeepEditing,
  onSave
}) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");

  // Reopening starts blank so a reason typed and abandoned is not carried over.
  useEffect(() => {
    if (!isOpen) setReason("");
  }, [isOpen]);

  return (
    <Modal
      open={isOpen}
      setIsOpen={(open) => !open && onKeepEditing()}
      contentClassname="w-[30rem]"
      data-test="save-terms-modal"
      title={
        <Text as="div" variant="lgSubtitleH1">
          {t("contracts.updateTerms.saveModal.title")}
        </Text>
      }
      content={
        <div className="flex flex-col gap-4 p-4">
          {shortensService ? (
            <div
              className="flex items-start gap-2 rounded-lg bg-badges-warning px-3 py-2.5"
              data-test="save-terms-early-termination"
            >
              <IconContainer size="sm" color="warning" className="shrink-0">
                <Alert />
              </IconContainer>
              <div className="flex flex-col">
                <Text variant="smBodyWarning">
                  {t("contracts.updateTerms.saveModal.earlyTerminationTitle")}
                </Text>
                <Text variant="smBodyWarning">
                  {t("contracts.updateTerms.saveModal.earlyTerminationMessage")}
                </Text>
              </div>
            </div>
          ) : null}
          <div className="flex flex-col">
            <Text variant="body">
              {t("contracts.updateTerms.saveModal.reasonLabel")}
            </Text>
            <Text variant="bodySecondary">
              {t("contracts.updateTerms.saveModal.reasonSubtitle")}
            </Text>
          </div>
          <Textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder={t("contracts.updateTerms.saveModal.reasonPlaceholder")}
            rows={4}
            maxLength={8192}
            className="rounded-lg border-[0.5px] border-border-field-default bg-surface-field-default resize-y"
            data-test="save-terms-reason"
          />
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="secondaryNoBg"
            onClick={onKeepEditing}
            data-test="save-terms-keep-editing"
          >
            {t("contracts.updateTerms.saveModal.keepEditing")}
          </Button>
          <Button
            disabled={isSaving}
            onClick={() => onSave(reason)}
            data-test="save-terms-confirm"
          >
            {t("contracts.updateTerms.saveModal.save")}
          </Button>
        </div>
      }
      hideDivider
    />
  );
};

export default SaveTermsModal;
